/**
 * Browser-only chat persistence (localStorage, like the portal's Zustand `persist`):
 * stores just the turns + KS conversationId so a reload restores the conversation.
 */
import type { Middleware } from "@reduxjs/toolkit";

export const CHAT_STORAGE_PREFIX = "ts_chat_v1_";

// One-time purge of the legacy un-isolated storage key.
try {
  localStorage.removeItem("ts_chat_v1");
} catch {
  /* ignore */
}

export function getChatStorageKey(projectId: string): string {
  return `${CHAT_STORAGE_PREFIX}${projectId}`;
}

interface PersistedTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: unknown[];
  streaming?: boolean;
  status?: string;
  excerptProposals?: { status?: string }[];
}
interface PersistedChat {
  turns: PersistedTurn[];
  conversationId: string | null;
}

// Load and sanitize the saved conversation for a project. Any turn caught
// mid-stream when the tab closed is reset to a settled state so it doesn't
// rehydrate as "thinking".
export function loadPersistedChat(projectId?: string | null): PersistedChat | null {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(getChatStorageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedChat;
    if (!parsed || !Array.isArray(parsed.turns)) return null;
    // An excerpt add in flight at close settles back to idle (confirm is idempotent).
    const turns = parsed.turns.map((t) => ({
      ...t,
      streaming: false,
      status: undefined,
      ...(t.excerptProposals && {
        excerptProposals: t.excerptProposals.map((p) => (p.status === "adding" ? { ...p, status: "idle" } : p)),
      }),
    }));
    return { turns, conversationId: parsed.conversationId ?? null };
  } catch {
    return null;
  }
}

export function savePersistedChat(projectId: string, chat: { turns?: unknown; conversationId?: unknown }): void {
  if (!projectId) return;
  try {
    localStorage.setItem(
      getChatStorageKey(projectId),
      JSON.stringify({ turns: chat.turns ?? [], conversationId: chat.conversationId ?? null })
    );
  } catch {
    /* storage full or unavailable — persistence is best-effort */
  }
}

export function clearPersistedChat(projectId?: string | null): void {
  if (!projectId) return;
  try {
    localStorage.removeItem(getChatStorageKey(projectId));
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

// Persist only after "commit" actions (not on every streaming delta) to avoid a
// write per token. resetChat clears storage.
const COMMIT_ACTIONS = new Set([
  "chat/addUserTurn",
  "chat/finishTurn",
  "chat/failTurn",
  "chat/setConversationId",
  "chat/mergeCitationMeta",
]);

export const chatPersistMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);
  const act = action as { type?: string; payload?: unknown };
  const type = act?.type;
  if (type === "chat/resetChat") {
    const targetProjectId =
      (typeof act.payload === "string" ? act.payload : null) ||
      (store.getState() as { chat?: { projectId?: string | null } })?.chat?.projectId;
    if (targetProjectId) {
      clearPersistedChat(targetProjectId);
    }
  } else if (type && COMMIT_ACTIONS.has(type)) {
    try {
      const chat = (
        store.getState() as { chat?: { projectId?: string | null; turns?: unknown; conversationId?: unknown } }
      ).chat;
      if (chat?.projectId) {
        savePersistedChat(chat.projectId, {
          turns: chat.turns ?? [],
          conversationId: chat.conversationId ?? null,
        });
      }
    } catch {
      /* storage full or unavailable — persistence is best-effort */
    }
  }
  return result;
};
